import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import {
  AuthResponse,
  passwordResetCheckFailed,
  passwordResetCheckSuccess,
  passwordResetForm,
  passwordResetFormFailed,
  passwordResetFormSuccess,
  passwordResetLinkFailed,
  passwordResetLinkSuccess,
  registeringFailed,
  registeringRequest,
  registeringSuccess,
  resendTokenRequest,
  resendTokenSuccess,
  resetCheckRequest,
  resetLinkRequest,
  verificationCodeFailed,
  verificationCodeRequest,
  verificationCodeSuccess,
  verificationFailed,
  verificationRequest,
  verificationSuccess,
  changePasswordRequest,
  changePasswordSuccess,
  changePasswordFailed,
} from './store/auth-actions';
import { catchError, exhaustMap, map, of, tap } from 'rxjs';
import { AuthService } from './auth-service';

@Injectable({
  providedIn: 'root',
})
@Injectable({ providedIn: 'root' })
export class AuthenticationEffects {
  private actions$ = inject(Actions);
  private authService = inject(AuthService);

  // REGISTER
  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(registeringRequest),
      exhaustMap(({ request }) =>
        this.authService.register(request).pipe(
          map((response) =>
            registeringSuccess({
              response: { success: true, message: 'Successfully', data: response, statusCode: 200 },
            })
          ),
          catchError((error) =>
            of(
              registeringFailed({
                error: { success: false, message: 'Failed', data: error?.error, statusCode: 400 },
              })
            )
          )
        )
      )
    )
  );

  // VERIFY TOKEN
  verifyToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verificationRequest),
      exhaustMap(({ request }) =>
        this.authService.verifyToken(request).pipe(
          map((response) =>
            verificationSuccess({
              response: { success: true, message: 'Successfully', data: response, statusCode: 200 },
            })
          ),
          catchError((error) =>
            of(
              verificationFailed({
                error: { success: false, message: 'Failed', data: error?.error, statusCode: 400 },
              })
            )
          )
        )
      )
    )
  );

  // VERIFY CODE
  verifyCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verificationCodeRequest),
      exhaustMap(({ request }) =>
        this.authService.verifyCode(request).pipe(
          map((response) => {
            return verificationCodeSuccess({
              response: { success: true, message: 'Successfully', data: response, statusCode: 200 },
            });
          }),
          catchError((error) =>
            of(
              verificationCodeFailed({
                error: { success: false, message: 'Failed', data: error?.error, statusCode: 400 },
              })
            )
          )
        )
      )
    )
  );

  // RESEND TOKEN - FIXED (was wrong before!)
  resendToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resendTokenRequest),
      exhaustMap(({ request }) =>
        this.authService.resendToken(request).pipe(
          map((response) =>
            resendTokenSuccess({
              response: { success: true, message: 'Successfully', data: response, statusCode: 200 },
            })
          ),
          catchError((error) =>
            of(
              verificationFailed({
                error: { success: false, message: 'Failed', data: error?.error, statusCode: 400 },
              })
            )
          )
        )
      )
    )
  );

  // PASSWORD RESET CHECK
  passwordResetCheck$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetCheckRequest),
      exhaustMap(({ request }) =>
        this.authService.passwordResetCheck(request).pipe(
          map((response) =>
            passwordResetCheckSuccess({
              response: { success: true, message: 'Successfully', data: response, statusCode: 200 },
            })
          ),
          catchError((error) =>
            of(
              passwordResetCheckFailed({
                error: { success: false, message: 'Failed', data: error?.error, statusCode: 400 },
              })
            )
          )
        )
      )
    )
  );

  // PASSWORD RESET LINK
  passwordResetLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetLinkRequest),
      exhaustMap(({ request }) =>
        this.authService.passwordResetLink(request).pipe(
          map((response) =>
            passwordResetLinkSuccess({
              response: { success: true, message: 'Successfully', data: response, statusCode: 200 },
            })
          ),
          catchError((error) =>
            of(
              passwordResetLinkFailed({
                error: { success: false, message: 'Failed', data: error?.error, statusCode: 400 },
              })
            )
          )
        )
      )
    )
  );

  // PASSWORD RESET FORM
  passwordResetForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(passwordResetForm),
      exhaustMap(({ request }) =>
        this.authService.resetPasswordConfirm(request).pipe(
          map((response) =>
            passwordResetFormSuccess({
              response: { success: true, message: 'Successfully', data: response, statusCode: 200 },
            })
          ),
          catchError((error) =>
            of(
              passwordResetFormFailed({
                error: { success: false, message: 'Failed', data: error?.error, statusCode: 400 },
              })
            )
          )
        )
      )
    )
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changePasswordRequest),
      exhaustMap(({ request }) =>
        this.authService.changePassword(request).pipe(
          map((response) => {
            const payload = response.data?.changePassword;
            if (!payload || !payload.status) {
              return changePasswordFailed({
                error: {
                  success: false,
                  message: payload?.message ?? 'Password change failed',
                  data: payload,
                  statusCode: 400,
                },
              });
            }
            return changePasswordSuccess({
              response: {
                success: true,
                message: payload?.message ?? 'Password changed successfully',
                data: payload,
                statusCode: 200,
              },
            });
          }),
          catchError((error) =>
            of(
              changePasswordFailed({
                error: {
                  success: false,
                  message: error?.message ?? 'Failed to change password',
                  data: null,
                  statusCode: 400,
                },
              })
            )
          )
        )
      )
    )
  );
}
